import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import * as ActionTypes from '../components/constants/performanceMetricsTableActions';
import * as MetricTitles from '../components/constants/performanceMetricsTableColumnTitles';
import { FieldNames } from '../components/constants/performanceMetricsTableFieldNames';
import { PERFORMANCE_METRIC_STATUSES } from '@shared/constants/checkpoint-performance-report-metric-enums';
import { PAGE_VIEW_MODES } from '../../../constants/page-view-modes';
import MultilineTextWithTooltip from '@shared/components/MultilineTextWithTooltip';

const usePerformanceMetricsTableColumns = (
  orgUnitId,
  appraisalId,
  viewMode,
  renderNumberInput,
  renderNotesTextInput,
  renderSelect,
  handleEditPerformanceMetric,
  checkPointId
) => {
  return useMemo(() => {
    if (viewMode != PAGE_VIEW_MODES.VIEW) {
      return [
        {
          title: MetricTitles.Metric,
          dataIndex: 'measurementName',
          width: '13%',
          sorter: (a, b) => sortMeasurementNameColumn(a.measurementName, b.measurementName),
          render: (text, record) => (record.status ? text : '-')
        },
        {
          title: MetricTitles.UpperLimit,
          dataIndex: 'upperLimit',
          width: '13%',
          render: (text, record) =>
            renderNumberInput(
              MetricTitles.UpperLimit,
              text,
              record.tempId,
              MetricTitles.UpperLimit,
              record.status,
              FieldNames.upperLimit
            )
        },
        {
          title: MetricTitles.LowerLimit,
          dataIndex: 'lowerLimit',
          width: '13%',
          render: (text, record) =>
            renderNumberInput(
              MetricTitles.LowerLimit,
              text,
              record.tempId,
              MetricTitles.LowerLimit,
              record.status,
              FieldNames.lowerLimit
            )
        },
        {
          title: MetricTitles.Actual,
          dataIndex: 'actual',
          width: '13%',
          render: (text, record) =>
            renderNumberInput(
              MetricTitles.Actual,
              text,
              record.tempId,
              record.referenceCheckpointDatapoint && record.referenceCheckpointDatapoint.actual
                ? String(record.referenceCheckpointDatapoint.actual)
                : MetricTitles.Actual,
              record.status,
              FieldNames.actual
            )
        },
        {
          title: MetricTitles.Notes,
          dataIndex: 'notes',
          width: '28%',
          render: (text, record) =>
            renderNotesTextInput(
              text,
              record.tempId,
              record.referenceCheckpointDatapoint && record.referenceCheckpointDatapoint.notes
                ? record.referenceCheckpointDatapoint.notes
                : MetricTitles.Notes,
              record.status
            )
        },
        {
          title: MetricTitles.Status,
          dataIndex: 'status',
          width: '12%',
          sorter: (a, b) => sortStatusColumn(a.status, b.status),
          render: (status, record) => {
            return renderSelect(status, record, record.addedInCurrentCheckpoint);
          }
        },
        {
          title: 'Action',
          dataIndex: 'Action',
          width: '8%',
          sorter: (a, b) => sortActionsColumn(a, b),
          render: (_, record) => {
            if (record.action === ActionTypes.EDIT) {
              return (
                <Link
                  to={`/check-points/${orgUnitId}/${viewMode}-check-point/${appraisalId}/${checkPointId}/edit-metric`}
                  onClick={() => handleEditPerformanceMetric(record.tempId)}>
                  Edit
                </Link>
              );
            }
            if (record.action === ActionTypes.ADD) {
              return (
                <Link
                  to={`/check-points/${orgUnitId}/${viewMode}-check-point/${appraisalId}/${checkPointId}/add-metric`}>
                  Add
                </Link>
              );
            }
          }
        }
      ];
    }

    return [
      {
        title: MetricTitles.Metric,
        dataIndex: 'measurementName',
        width: '13%',
        sorter: (a, b) => sortMeasurementNameColumn(a.measurementName, b.measurementName),
        render: fieldRenderer
      },
      {
        title: MetricTitles.UpperLimit,
        dataIndex: 'upperLimit',
        width: '13%',
        render: fieldRenderer
      },
      {
        title: MetricTitles.LowerLimit,
        dataIndex: 'lowerLimit',
        width: '13%',
        render: fieldRenderer
      },
      {
        title: MetricTitles.Actual,
        dataIndex: 'actual',
        width: '13%',
        render: fieldRenderer
      },
      {
        title: MetricTitles.Notes,
        dataIndex: 'notes',
        width: '28%',
        render: value => <MultilineTextWithTooltip value={fieldRenderer(value)} maxRowNumber={1} />
      },
      {
        title: MetricTitles.Status,
        dataIndex: 'status',
        width: '12%',
        sorter: (a, b) => sortStatusColumn(a.status, b.status),
        render: metricStatusFieldRenderer
      },
      {
        title: 'Action',
        dataIndex: 'Action',
        width: '8%',
        render: (_, record) => {
          return (
            <Link
              to={
                `/check-points/${orgUnitId}/view-check-point/${appraisalId}/${checkPointId}/view-metric/` +
                `${record.dataPointId}`
              }>
              View
            </Link>
          );
        }
      }
    ];
  }, [
    orgUnitId,
    appraisalId,
    viewMode,
    renderNumberInput,
    renderNotesTextInput,
    renderSelect,
    handleEditPerformanceMetric,
    checkPointId
  ]);
};

const sortMeasurementNameColumn = (a, b) => {
  if (!a.length || !b.length) {
    return;
  }
  return a.localeCompare(b);
};

const sortStatusColumn = (a, b) => {
  if (typeof a != 'number' || typeof b != 'number') {
    return;
  }
  const statusTitleForA = metricStatusFieldRenderer(a);
  const statusTitleForB = metricStatusFieldRenderer(b);
  return statusTitleForA.localeCompare(statusTitleForB);
};

const sortActionsColumn = (a, b) => {
  if (a <= b) {
    return -1;
  }
  if (b < a) {
    return 1;
  }
};

const metricStatusFieldRenderer = statusId => {
  const key = Object.keys(PERFORMANCE_METRIC_STATUSES).find(
    s => PERFORMANCE_METRIC_STATUSES[s].Id === Number(statusId)
  );
  return PERFORMANCE_METRIC_STATUSES[key].Value;
};

const fieldRenderer = field => (field != null ? field : '-');

export default usePerformanceMetricsTableColumns;
